import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { genresData } from "../../../constants/structure";
import { RoomFilters } from "../../../api/room";
import styles from "./RoomEntry.module.scss";

interface RoomEntryProps {
  onCreateRoom: (filters: RoomFilters) => void;
  onJoinRoom: (code: string) => void;
  loading: boolean;
  error: string;
}

const RoomEntry: React.FC<RoomEntryProps> = ({
  onCreateRoom,
  onJoinRoom,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const [joinCode, setJoinCode] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [minRating, setMinRating] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");

  const handleCreate = () => {
    const filters: RoomFilters = {};
    if (selectedGenres.length) filters.genre_ids = selectedGenres;
    if (minRating) filters.min_rating = parseFloat(minRating);
    if (yearFrom) filters.year_from = parseInt(yearFrom);
    if (yearTo) filters.year_to = parseInt(yearTo);
    onCreateRoom(filters);
  };

  const handleJoin = () => {
    if (!joinCode.trim()) return;
    onJoinRoom(joinCode.trim().toUpperCase());
  };

  const toggleGenre = (id: number) => {
    setSelectedGenres((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  return (
    <div className={styles.entry}>
      <div className={styles.title}>{t("room_title", "Movie Room")}</div>
      <div className={styles.subtitle}>
        {t("room_subtitle", "Create a room and watch movies together with friends")}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t("room_create", "Create a room")}
        </div>

        {/* Genre selection */}
        <div className={styles.genreGrid}>
          {genresData.map((genre) => (
            <button
              key={genre.id}
              className={`${styles.genreChip} ${
                selectedGenres.includes(genre.id) ? styles.genreChipActive : ""
              }`}
              onClick={() => toggleGenre(genre.id)}
            >
              {t(genre.name)}
            </button>
          ))}
        </div>

        <div className={styles.filtersGrid}>
          <div className={styles.filterField}>
            <label>{t("room_min_rating", "Min rating")}</label>
            <input
              type="number"
              min="0"
              max="10"
              step="0.5"
              placeholder="e.g. 7.0"
              value={minRating}
              onChange={(e) => setMinRating(e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <label>{t("room_year_from", "Year from")}</label>
            <input
              type="number"
              min="1900"
              max="2030"
              placeholder="e.g. 2000"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <label>{t("room_year_to", "Year to")}</label>
            <input
              type="number"
              min="1900"
              max="2030"
              placeholder="e.g. 2024"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
            />
          </div>
        </div>

        <button
          className={styles.btnPrimary}
          onClick={handleCreate}
          disabled={loading}
        >
          {loading
            ? t("room_creating", "Creating...")
            : t("room_create_btn", "Create Room")}
        </button>
      </div>

      <div className={styles.divider}>
        <span>{t("or", "or")}</span>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t("room_join", "Join a room")}
        </div>
        <div className={styles.joinRow}>
          <input
            maxLength={6}
            placeholder={t("room_code_placeholder", "Enter code")}
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleJoin()}
          />
          <button
            className={styles.btnPrimary}
            onClick={handleJoin}
            disabled={loading || !joinCode.trim()}
          >
            {t("room_join_btn", "Join")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomEntry;
